import type { PricingConfig } from './pricing-config.js'

const LABEL_OVERRIDES: Record<string, string> = {
  QuarterSawnWhiteOak: 'Rift/Qtr. White Oak',
  StandardDropIn: 'Standard Drop-In',
  MediumDropIn: 'Medium Drop-In',
  LargeDropIn: 'Large Drop-In',
  CustomLeaf: 'Custom',
  ApronBasic: 'Basic',
  RoughSawn: 'Rough Sawn',
  NaturalWavy: 'Natural Wavy',
  MidModernTaperBase: 'Mid-Modern Taper',
  AnglePictureFrameCTCBase: 'Angle Picture Frame CTC',
  ReflectionCTCBase: 'Reflection CTC',
  SlimConnectedXBase: 'Slim Connected X',
  SlimConnectedXSingleBase: 'Slim Connected X (Single)',
  XMetalBase: 'X Metal',
  XMetalSingleBase: 'X Metal (Single)',
  XBase: 'X',
  XSingleBase: 'X (Single)',
  BlackTone: 'Black',
  EbonyTone: 'Ebony',
  DarkBrownTone: 'Dark Brown',
  MediumBrownTone: 'Medium Brown',
  LightBrownTone: 'Light Brown',
  NaturalTone: 'Natural',
}

function splitPascalCase(str: string): string {
  return str.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
}

const BASE_SUFFIX_RE = /\s*Base$/
const TOP_SUFFIX_RE = /\s*Top$/
const SINGLE_RE = /\s*Single\b\s*/

function formatBaseName(key: string): string {
  if (LABEL_OVERRIDES[key]) return LABEL_OVERRIDES[key]

  let label = splitPascalCase(key).replace(BASE_SUFFIX_RE, '')
  const isSingle = SINGLE_RE.test(label)
  label = label.replace(SINGLE_RE, ' ').trim()
  label = label.replace(/\s*(Feet|Platform)$/, (_, variant) => ` (${variant})`)
  if (isSingle) label += ' (Single)'
  return label
}

function formatTopName(key: string): string {
  if (LABEL_OVERRIDES[key]) return LABEL_OVERRIDES[key]
  return splitPascalCase(key).replace(TOP_SUFFIX_RE, '')
}

function formatGenericName(key: string): string {
  if (LABEL_OVERRIDES[key]) return LABEL_OVERRIDES[key]
  return splitPascalCase(key)
}

export function formatLabel(key: string, category: string): string {
  if (category === 'bases') return formatBaseName(key)
  if (category === 'tops') return formatTopName(key)
  return formatGenericName(key)
}

type SimplePricingCategory = keyof {
  [K in keyof PricingConfig as PricingConfig[K] extends Record<string, number> ? K : never]: true
}

type TopsPricingCategory = keyof {
  [K in keyof PricingConfig as PricingConfig[K] extends Record<
    string,
    { basePrice: number; compoundingRate: number; pricingStep: number }
  >
    ? K
    : never]: true
}

type ScalarPricingCategory = keyof {
  [K in keyof PricingConfig as PricingConfig[K] extends number ? K : never]: true
}

type PricingGroupSection =
  | { readonly type: 'tops'; readonly category: TopsPricingCategory; readonly label: string }
  | { readonly type: 'simple'; readonly category: SimplePricingCategory; readonly label: string }
  | { readonly type: 'scalar'; readonly category: ScalarPricingCategory; readonly label: string }

export type PricingGroup = {
  readonly label: string
  readonly sections: readonly PricingGroupSection[]
}

export const BASE_SUBCATEGORIES: Record<string, readonly string[]> = {
  Pedestal: [
    'AnchorBase',
    'AnchorSingleFeetBase',
    'AnchorSinglePlatformBase',
    'BellhouseBase',
    'BellhouseSingleFeetBase',
    'BellhouseSinglePlatformBase',
    'BellyStretchedBase',
    'BrewerBase',
    'BrewerSingleBase',
    'CaseyBase',
    'CraftsmanBase',
    'CraftsmanSingleBase',
    'CrescentBase',
    'CrescentSingleBase',
    'DanaBase',
    'DanaSingleBase',
    'FranklinBase',
    'FranklinSingleFeetBase',
    'FranklinSinglePlatformBase',
    'MarksBase',
    'MeredithBase',
    'MeredithSingleFeetBase',
    'MeredithSinglePlatformBase',
    'OswaldBase',
    'OswaldSingleBase',
    'PeruStretchedBase',
    'ReededBase',
    'RishBase',
    'RishSingleBase',
    'RogersBase',
    'RogersSinglePlatformBase',
    'TamaraBase',
    'TamaraSingleBase',
    'TuscanBase',
    'TuscanSingleFeetBase',
    'TuscanSinglePlatformBase',
    'WaldenBase',
    'WaldenSingleBase',
    'XBase',
    'XSingleBase',
  ],
  Metal: [
    'AnglePictureFrameBase',
    'AnglePictureFrameSingleBase',
    'AnglePictureFrameCTCBase',
    'ArchBase',
    'ArchSingleBase',
    'BoxFrameBase',
    'ConnectedXBase',
    'MantisBase',
    'SlimConnectedXBase',
    'SlimConnectedXSingleBase',
    'TriflectionBase',
    'ReflectionBase',
    'ReflectionCTCBase',
    'SinglePostBase',
    'SinglePostSingleBase',
    'XMetalBase',
    'XMetalSingleBase',
  ],
  'Four Leg': [
    'IvyBase',
    'DremorTurnedBase',
    'HerricksonTurnedBase',
    'MidModernTaperBase',
    'NarrowEnglishTurnedBase',
    'OldEnglishTurnedBase',
    'SlagleTurnedBase',
    'TraditionalBase',
    'TraditionalFlushBase',
  ],
}

const HIDDEN_PRICING_KEYS: Partial<Record<string, ReadonlySet<string>>> = {
  aprons: new Set(['ApronNone']),
  seating: new Set(['NoSeating', 'Chairs', 'ChairsAndBenches']),
  leaves: new Set(['NoLeaf']),
  thickness: new Set(['Standard']),
  distressLevels: new Set(['Clean']),
}

export function filterHiddenKeys(data: Record<string, number>, category: string): Record<string, number> {
  const hidden = HIDDEN_PRICING_KEYS[category]
  if (!hidden) return data

  const result: Record<string, number> = {}
  for (const [key, value] of Object.entries(data)) {
    if (!hidden.has(key)) {
      result[key] = value
    }
  }
  return result
}

const ALL_CATEGORIZED_BASES = new Set(Object.values(BASE_SUBCATEGORIES).flat())

export function getUncategorizedBases(bases: Record<string, number>): Record<string, number> {
  const result: Record<string, number> = {}
  for (const [key, value] of Object.entries(bases)) {
    if (!ALL_CATEGORIZED_BASES.has(key)) {
      result[key] = value
    }
  }
  return result
}

export function getBaseSubcategoryData(bases: Record<string, number>, subcategory: string): Record<string, number> {
  const keys = BASE_SUBCATEGORIES[subcategory] ?? []
  const result: Record<string, number> = {}
  for (const key of keys) {
    if (key in bases) {
      result[key] = bases[key] ?? 0
    }
  }
  return result
}

export const PRICING_GROUPS: readonly PricingGroup[] = [
  {
    label: 'Table Top',
    sections: [
      { type: 'tops', category: 'tops', label: 'Top Shapes' },
      { type: 'simple', category: 'thickness', label: 'Thickness' },
      { type: 'simple', category: 'edgeStyles', label: 'Edge Styles' },
    ],
  },
  {
    label: 'Base',
    sections: [
      { type: 'simple', category: 'bases', label: 'Bases' },
      { type: 'scalar', category: 'crossbrace', label: 'Cross Brace' },
    ],
  },
  {
    label: 'Material & Finish',
    sections: [
      { type: 'simple', category: 'woodSpecies', label: 'Wood Species (% of top price)' },
      { type: 'simple', category: 'distressLevels', label: 'Distress Levels' },
      { type: 'simple', category: 'finishes', label: 'Finishes' },
      { type: 'simple', category: 'sheens', label: 'Sheens' },
    ],
  },
  {
    label: 'Accessories',
    sections: [
      { type: 'simple', category: 'leaves', label: 'Leaves' },
      { type: 'simple', category: 'aprons', label: 'Aprons' },
      { type: 'simple', category: 'seating', label: 'Seating' },
      { type: 'scalar', category: 'grooves', label: 'Grooves' },
      { type: 'scalar', category: 'cornerRadius', label: 'Corner Radius' },
    ],
  },
]
